import { createContext, ReactNode, useContext, useMemo } from "react";
import { useAuth } from "@mesterplan/auth-react";
import type { ApiResult, CurrentUserDto } from "@mesterplan/api-client";
import { useApiQuery, useMesterplanApi } from "@mesterplan/web-hooks";
import { useActiveOrgId } from "@/contexts/NetClientsContext";
import { idpEnabled } from "@/lib/idp";

export interface NetUserState {
  /** The .NET current-user record (incl. server-resolved effective permissions). */
  user: CurrentUserDto | null;
  /** True while OIDC is initialising or the first `/me` for the active org is in flight. */
  isLoading: boolean;
}

const INERT: NetUserState = { user: null, isLoading: false };

const NetUserContext = createContext<NetUserState>(INERT);

/**
 * Shared accessor for the .NET current-user record. Reads the single
 * <NetUserProvider> fetch instead of issuing its own `GET /api/users/me`, so the
 * many consumers (usePermissions at ~25 sites, ProtectedRoute, the sidebar,
 * useNetCompany, ProfileSettings) collapse onto one request per active org.
 */
export function useNetUserContext(): NetUserState {
  return useContext(NetUserContext);
}

/**
 * Fetches `GET /api/users/me` once and shares it via context. Mirrors
 * CompanyProvider: only the IDP path mounts the real fetcher (its hooks throw
 * outside NetClientsProvider). Firebase mode gets an inert default so shared
 * consumers stay safe in both auth modes.
 */
export function NetUserProvider({ children }: { children: ReactNode }) {
  return idpEnabled ? (
    <NetUserFetcher>{children}</NetUserFetcher>
  ) : (
    <NetUserContext.Provider value={INERT}>{children}</NetUserContext.Provider>
  );
}

function NetUserFetcher({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const activeOrgId = useActiveOrgId();
  const api = useMesterplanApi();

  const enabled = status === "authenticated";

  // Re-fetch when the active org changes so `permissions` reflects X-Active-Org
  // (the server resolves the effective set per active org). `api` is in the deps
  // for exhaustive-deps correctness; it is memoised in NetClientsProvider (stable
  // for the provider's lifetime), so this never causes an extra fetch in
  // practice — it just future-proofs against the client reference changing.
  const query = useApiQuery<CurrentUserDto>(
    () =>
      (enabled
        ? api.users.me()
        : Promise.resolve({
            ok: false as const,
            status: 0,
            error: { status: 0, detail: "not-authenticated" },
          })) as Promise<ApiResult<CurrentUserDto>>,
    [enabled, activeOrgId, api],
  );

  // useApiQuery resets in an effect. During the first render after an org
  // change its previous successful response may still be present. Do not expose
  // that tenant's permissions (or redirect a deep link using the unscoped set).
  const scopePending = enabled && query.status === "success" && query.data?.activeOrgId !== activeOrgId;

  const value = useMemo<NetUserState>(
    () => ({
      user: enabled && query.status === "success" && !scopePending ? query.data : null,
      isLoading: status === "initializing" || (enabled && (query.status === "loading" || scopePending)),
    }),
    [enabled, status, query.status, query.data, scopePending],
  );

  return <NetUserContext.Provider value={value}>{children}</NetUserContext.Provider>;
}
